/* eslint-disable no-void */
import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import {
  Typography,
  Container,
  Box,
  Card,CardMedia,CardContent,
  Paper,
  TableContainer,Table,TableHead,TableRow,TableCell,TableBody
} from '@mui/material/';
import Grid from '@mui/material/Grid2';
import { ROLE_KEYS, useUserRole } from '../../../utils/awsConfigure';
import useNotification from "../../../utils/notificationUtil";
import useConfirmDialog from '../../../components/ConfirmDialog'
import {EQUIPMENT_URLS, URL_PARAMS, DISP_MODE} from '../../../constants/constants'
import { DataStoreContext, CONTEXT_ACTION_TYPE, initialState } from "../contexts/EquipmentContext";

import {ResponseType, deleteAttachment, findHand, findArm, findAttachment} from '../../../utils/awsAmplifyUtil'
import { AttachmentDocument, attachmentInitDoc , makeAttachmentAttrList, makeFormAttachmentFromDocument } from '../types/pj1dbApiAttachment'
import { HandDocument } from '../types/pj1dbApiHand';
import { ArmDocument } from '../types/pj1dbApiArm';
import { AttrListRowType } from '../types/handForm';

import Pjdb1RadioGroup from '../../../components/inputs/Pjdb1RadioGroup';
import Pjdb1Button from '../../../components/inputs/Pjdb1Button';
import UrdfLoader from '../../../components/UrdfLoader';
import SearchingMsg from '../../../components/SearchingMsg';

const IMAGE_WIDTH = 240;
const IMAGE_HEIGHT = 240;
const CARD_HEIGHT = IMAGE_HEIGHT + 55;

export default function AttachmentDetail() {
  const { userRole, userDomain } = useUserRole();
  const [searchParams] = useSearchParams();
  const isPopupMode = !!(searchParams.get(URL_PARAMS.DISP_MODE) && searchParams.get(URL_PARAMS.DISP_MODE) === DISP_MODE.POPUP);
  const { deletedMsg, errorMsg } = useNotification();
  const {ConfirmDialog, openConfirmDialog} = useConfirmDialog();

  const { state, dispatch } = useContext(DataStoreContext);
  const [isLoading, setIsLoading] = useState(false);

  const [info, setInfo] = useState<AttachmentDocument>(attachmentInitDoc);
  const [attachmentAttrList, setAttachmentAttrList] = useState<AttrListRowType[]>([]);
  const [dispImageType, setDispImageType] = useState<string>("image");

  interface ImageListRowType {
    key:string;
    url:string;
  }
  const [attachmentImageList, setAttachmentImageList] = useState<ImageListRowType[]>([]);
  const [selectedAttachmentImageKey, setSelectedAttachmentImageKey] = useState<string>("");

  const [handList, setHandList] = useState<HandDocument[]>([]);
  const [searchingHand, setSearchingHand] = useState(true);
  const [armList, setArmList] = useState<ArmDocument[]>([]);
  const [searchingArm, setSearchingArm] = useState(true);

  const navigate = useNavigate();
  const urlParams = useParams<{ attachmentId: string }>();

  const imageStyle = {
    border : "1px solid rgba(0, 0, 0, 0.23)",
  }
  const selectedImageStyle = {
    border : "2px solid #48B130",
  }

  const findAttachmentProc = async (attachmentId:string) => {
    try {
      const result = await findAttachment({"query":{"attachmentId":attachmentId}});
      if(!result.isSuccess) return;
      let attachmentListData:AttachmentDocument[] = [];
      if(result.data && Object.entries(result.data).length > 0){
        attachmentListData = Object.entries(result.data).map(([,attachmentDoc])=>(attachmentDoc));
      }
      if(attachmentListData.length===0){
        dispatch({ type: CONTEXT_ACTION_TYPE.TAB , payload: EQUIPMENT_URLS.ATTACHMENT_LIST.TAB_IDX });
        navigate(`${EQUIPMENT_URLS.ATTACHMENT_LIST.URL}`);
      }else{
        setInfo(attachmentListData[0]);
        dispatch({ type: CONTEXT_ACTION_TYPE.TAB , payload: EQUIPMENT_URLS.ATTACHMENT_DETAIL.TAB_IDX });
        dispatch({ type: CONTEXT_ACTION_TYPE.ATTACHMENT_DETAIL , payload: {info: attachmentListData[0]} });
      }
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(()=>{
    let tempInfo:AttachmentDocument;
    if(urlParams.attachmentId===state.attachmentDetailPage.info.attr.attachmentId){
      tempInfo = state.attachmentDetailPage.info;
    }else if (!urlParams.attachmentId){
      tempInfo = state.attachmentDetailPage.info;
    }else{
      [tempInfo] =
        state.attachmentListPage.attachmentList.filter(
          (item)=>item.attr.attachmentId === urlParams.attachmentId
        );
    }
    if(tempInfo!==undefined){
      setInfo(tempInfo);
      return;
    }

    // 別機能からの遷移、情報検索して表示する
    if (urlParams.attachmentId){
      void findAttachmentProc(urlParams.attachmentId);
    }else{
      dispatch({ type: CONTEXT_ACTION_TYPE.TAB , payload: EQUIPMENT_URLS.ATTACHMENT_LIST.TAB_IDX });
      navigate(`${EQUIPMENT_URLS.ATTACHMENT_LIST.URL}`);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const findHandProc = async () => {
    try {
      const cond = {
        "query":{
          "handId":{ "$in" : info.attr.handIds }
        }
      };
      const result = await findHand(cond);
      if(!result.isSuccess) return;
      if(result.data && Object.entries(result.data).length > 0){
        let handListData:HandDocument[] = Object.entries(result.data).map(([,handDoc])=>(handDoc));
        handListData = handListData.sort((a,b) => a.attr.handInfo.name.localeCompare(b.attr.handInfo.name));
        setHandList(handListData);
      }else{
        setHandList([]);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setSearchingHand(false);
    }
  }

  const findArmProc = async () => {
    try {
      const cond = {
        "query":{
          "armId":{ "$in" : info.attr.armIds }
        }
      };
      const result = await findArm(cond);
      if(!result.isSuccess) return;
      if(result.data && Object.entries(result.data).length > 0){
        let armListData:ArmDocument[] = Object.entries(result.data).map(([,armDoc])=>(armDoc));
        armListData = armListData.sort((a,b) => a.attr.armInfo.name.localeCompare(b.attr.armInfo.name));
        setArmList(armListData);
      }else{
        setArmList([]);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setSearchingArm(false);
    }
  }

  useEffect(() => {
    if(info.attr.attachmentId==="") return;

    // アタッチメント属性表
    setAttachmentAttrList(makeAttachmentAttrList(info));

    // 画像リストを作成
    if(info.url){
      setAttachmentImageList(
        info.url?.image.map((imageUrl,idx)=>(
          {key:`image${idx}`,url:imageUrl}
        ))
      );
      setSelectedAttachmentImageKey("image0");
    }

    // 接続可能ハンドを検索
    void findHandProc();
    // 接続可能アームを検索
    void findArmProc();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[info]);

  const deleteData = async () => {
    try {
      console.log("deleteData",info.attr.attachmentId);
      // 確認ダイアログ
      const result = await openConfirmDialog();
      if (result !== "confirm") {
        return;
      }

      setIsLoading(true);

      const delData = {
        id:info.attr.attachmentId
      }
      const resDel:ResponseType<string> = await deleteAttachment(delData);
      if(!resDel.isSuccess){
        errorMsg("削除処理が失敗しました");
        return;
      }

      deletedMsg();

      dispatch({ type: CONTEXT_ACTION_TYPE.ATTACHMENT_DETAIL ,
        payload: {info: initialState.attachmentDetailPage.info} });

      const newList = state.attachmentListPage.attachmentList.filter(row=>row.attr.attachmentId!==info.attr.attachmentId);
      const newState = state.attachmentListPage;
      newState.attachmentList = newList;
      newState.searchCond.listCount = newList.length;
      dispatch({ type: CONTEXT_ACTION_TYPE.ATTACHMENT_LIST , payload: newState });

      dispatch({ type: CONTEXT_ACTION_TYPE.TAB , payload: EQUIPMENT_URLS.ATTACHMENT_LIST.TAB_IDX });
      navigate(`${EQUIPMENT_URLS.ATTACHMENT_LIST.URL}`);

    } catch (error) {
      console.log("deleteData Error : ", error);
      errorMsg("削除処理が失敗しました。");
    } finally {
      setIsLoading(false);
    }
  }
  const editData = () => {
    dispatch({ type: CONTEXT_ACTION_TYPE.REGIST ,
      payload: {...state.registPage,regModeAttachment:false,attachmentPanel:makeFormAttachmentFromDocument(info)} });
    dispatch({ type: CONTEXT_ACTION_TYPE.TAB , payload: EQUIPMENT_URLS.REGISTE.TAB_IDX });
    navigate(`${EQUIPMENT_URLS.REGISTE.URL}/attachment/${info.attr.attachmentId}`);
  }
  const copyData = () => {
    dispatch({ type: CONTEXT_ACTION_TYPE.REGIST ,
      payload: {...state.registPage,regModeAttachment:true,attachmentPanel:makeFormAttachmentFromDocument(info)} });
    dispatch({ type: CONTEXT_ACTION_TYPE.TAB , payload: EQUIPMENT_URLS.REGISTE.TAB_IDX });
    navigate(`${EQUIPMENT_URLS.REGISTE.URL}/copyAttachment/${info.attr.attachmentId}`);
  }

  return (
    <>
      <Container maxWidth="lg" sx={{maxWidth:"1500px !important"}}>
        <Card sx={{p:3,mt:2}}>

          <Grid container spacing={3} alignItems="flex-end" >
            <Grid>
              <Typography variant="h6">アタッチメント詳細</Typography>
            </Grid>
            <Grid sx={{flexGrow: 1}}>
              <Typography variant="body2" sx={{color:"gray"}}>
                登録日時（登録者）：
                  {`${info.attr?.registrationDate?new Date(info.attr?.registrationDate).toLocaleString():""}
                  （${userRole === ROLE_KEYS.ADMIN ? String(info.attr.registrant) : String(info.attr.domain)}）`}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={3} alignItems="flex-start" sx={{mt:1}}>
            <Grid size={{xs:12,md:6}} sx={{pt:"0px !important"}}>

              <Pjdb1RadioGroup
                name="dispImageType" value={dispImageType}
                onChange={(e)=>(setDispImageType(e.target.value))}
                radios={[
                  {btnKey:"image",btnLbl:"画像"},
                  {btnKey:"model",btnLbl:"モデル"},
                ]}
                disabledRadios={info.url?.urdf && info.url?.urdf.length > 0 ? [] : ["model"]}
              />

              <Grid container spacing={2} alignItems="flex-start" sx={{mt:0}}>
                { dispImageType === "image" &&
                  <>
                    <Grid size={{xs:3,md:2}}
                    sx={{pt:"0px !important",pr:"1px !important",height:"35vw",maxHeight:"58vh",overflowY:"auto",overflowX:"hidden"}}
                    className="thin-scroll-bar">
                      {attachmentImageList.map(row =>(
                        <Box key={row.key}>
                          <a href="" onClick={(e)=>{
                            e.preventDefault();
                            setSelectedAttachmentImageKey(row.key);
                          }}>
                            <img src={row.url} className="square-image" style={row.key === selectedAttachmentImageKey ? selectedImageStyle : imageStyle} alt={row.key}/>
                          </a>
                        </Box>
                      ))}
                    </Grid>
                    <Grid size={{xs:9,md:10}} sx={{pt:"0px !important"}}>
                      <img src={attachmentImageList.find((row)=>(row.key===selectedAttachmentImageKey))?.url} style={imageStyle} className="square-image" alt="" />
                    </Grid>
                  </>
                }
                { dispImageType === "model" &&
                  <Grid sx={{p:"0px !important",border:"1px solid rgba(0, 0, 0, 0.23)",position:"relative"}} className="square-image">
                    <UrdfLoader urdfUrl={info.url?.urdf[0]} mesheUrls={info.url?.meshes} />
                  </Grid>
                }
              </Grid>

            </Grid>
            <Grid size={{xs:12,md:6}} sx={{pt:"20px !important"}}>

              <Typography variant="subtitle1">アタッチメント属性表</Typography>
              <TableContainer component={Paper}>
                <Table sx={{ maxWidth: "100%" }} size="small" aria-label="a dense table">
                  <TableHead sx={{bgcolor:"#EEE"}}>
                    <TableRow>
                      <TableCell sx={{px:1,py:0.5}}>分類</TableCell>
                      <TableCell sx={{px:1,py:0.5}}>属性</TableCell>
                      <TableCell sx={{px:1,py:0.5}}>属性値</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attachmentAttrList.map((row) => (
                      <TableRow
                        key={row.key}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell width={80} sx={{px:1,py:0.5}}>{row.class}</TableCell>
                        <TableCell width={150} sx={{px:1,py:0.5}}>{row.attr}</TableCell>
                        <TableCell sx={{px:1,py:0.5}} dangerouslySetInnerHTML={{ __html: String(row.value) }} />
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

            </Grid>
          </Grid>

          <Grid container spacing={3} alignItems="flex-end" sx={{mt:1}}>
            <Grid sx={{pt:"8px !important"}}>
              <Typography variant="h6">接続可能ハンド一覧</Typography>
            </Grid>
          </Grid>

          { handList.length === 0 && searchingHand &&
            <SearchingMsg />
          }
          { handList.length === 0 && !searchingHand &&
            <Typography variant="subtitle1" sx={{m:2,fontWeight:"bold"}}>
              接続可能ハンドがありません。
            </Typography>
          }
          { handList.length > 0 &&
            <Grid container spacing={3} alignItems="flex-start" sx={{mt:1,m:2}}>
              {handList.map(row =>(
                <Grid key={row.attr.handId} >
                  <Link to={`${EQUIPMENT_URLS.HAND_DETAIL.URL}/${row.attr.handId}`}
                  style={{ textDecoration:'none' , ...(isPopupMode ? {cursor : "default"} : {}) }}
                  onClick={(e)=>{
                    if(isPopupMode){
                      e.preventDefault();
                      return;
                    }
                    dispatch({ type: CONTEXT_ACTION_TYPE.TAB , payload: EQUIPMENT_URLS.HAND_DETAIL.TAB_IDX });
                    dispatch({ type: CONTEXT_ACTION_TYPE.HAND_DETAIL , payload: {info:row} });
                  }}>

                    <Card sx={{ width:IMAGE_WIDTH, height:CARD_HEIGHT }}>
                      <CardMedia
                        component="img"
                        height={IMAGE_HEIGHT}
                        image={row.url?.image[0]}
                        alt={row.attr.handInfo.name}
                        sx={{objectFit:'contain'}}
                      />
                      <CardContent sx={{py:0.5,px:1,m:0}}>
                        <Typography variant="subtitle2" noWrap component='p' sx={{mt:0.5}}>
                          製品名：{row.attr.handInfo.name}
                        </Typography>
                        <Typography variant="subtitle2" noWrap component='p' sx={{color:"#888"}}>
                          製造者：{row.attr.handInfo.producer}
                        </Typography>
                      </CardContent>
                    </Card>

                  </Link>
                </Grid>
              ))}
            </Grid>
          }

          <Grid container spacing={3} alignItems="flex-end" sx={{mt:1}}>
            <Grid sx={{pt:"8px !important"}}>
              <Typography variant="h6">接続可能アーム一覧</Typography>
            </Grid>
          </Grid>

          { armList.length === 0 && searchingArm &&
            <SearchingMsg />
          }
          { armList.length === 0 && !searchingArm &&
            <Typography variant="subtitle1" sx={{m:2,fontWeight:"bold"}}>
              接続可能アームがありません。
            </Typography>
          }
          { armList.length > 0 &&
            <Grid container spacing={3} alignItems="flex-start" sx={{mt:1,m:2}}>
              {armList.map(row =>(
                <Grid key={row.attr.armId} >
                  <Link to={`${EQUIPMENT_URLS.ARM_DETAIL.URL}/${row.attr.armId}`}
                  style={{ textDecoration:'none' , ...(isPopupMode ? {cursor : "default"} : {}) }}
                  onClick={(e)=>{
                    if(isPopupMode){
                      e.preventDefault();
                      return;
                    }
                    dispatch({ type: CONTEXT_ACTION_TYPE.TAB , payload: EQUIPMENT_URLS.ARM_DETAIL.TAB_IDX });
                    dispatch({ type: CONTEXT_ACTION_TYPE.ARM_DETAIL , payload: {info:row} });
                  }}>

                    <Card sx={{ width:IMAGE_WIDTH, height:CARD_HEIGHT }}>
                      <CardMedia
                        component="img"
                        height={IMAGE_HEIGHT}
                        image={row.url?.image[0]}
                        alt={row.attr.armInfo.name}
                        sx={{objectFit:'contain'}}
                      />
                      <CardContent sx={{py:0.5,px:1,m:0}}>
                        <Typography variant="subtitle2" noWrap component='p' sx={{mt:0.5}}>
                          製品名：{row.attr.armInfo.name}
                        </Typography>
                        <Typography variant="subtitle2" noWrap component='p' sx={{color:"#888"}}>
                          製造者：{row.attr.armInfo.manufacturer}
                        </Typography>
                      </CardContent>
                    </Card>

                  </Link>
                </Grid>
              ))}
            </Grid>
          }


        </Card>
      </Container>
      { !isPopupMode &&
        <Box component="section" sx={{ textAlign:"center", mt:2 }}>
          { (userRole === ROLE_KEYS.ADMIN || userDomain===info.attr.domain) &&
            <Pjdb1Button
              label="登録情報を変更"
              variant="contained"
              color="primary"
              isLoading={isLoading}
              sx={{m:1}}
              onClick={editData}
              disabled={info.attr.attachmentId===""}
            />
          }

          { (userRole === ROLE_KEYS.ADMIN || userDomain===info.attr.domain) &&
            <Pjdb1Button
              label="登録情報を削除"
              variant="contained"
              color="error"
              isLoading={isLoading}
              sx={{m:1}}
              onClick={()=>{void deleteData();}}
              disabled={info.attr.attachmentId===""}
            />
          }

          <Pjdb1Button
            label="コピー登録"
            variant="contained"
            color="secondary"
            isLoading={isLoading}
            sx={{m:1}}
            onClick={copyData}
            disabled={info.attr.attachmentId===""}
          />
        </Box>
      }

      <ConfirmDialog
        title="削除確認"
        message="アーム情報を削除しますか？"
        confirmButton="削除"
        confirmButtonStyle="error"
      />
    </>

  )
}